import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, inject } from '@angular/core';
import { SubsectionTreeService } from '../../../project-subsection/services/subsection-tree.service';
import { distinctUntilChanged, fromEvent, map, startWith, Subject, takeUntil } from 'rxjs';
import {
  BaseTreeNode,
  isKpfRootNode,
  isProjectRootNode,
  isSubsectionFileNode,
  isSubsectionVersionNode,
  KpfFolderNode,
  ProjectNode,
  TreeState,
  TreeNodeType,
  ProjectRootNode,
} from '../../../project-subsection/models/tree-node';
import { TreeManagerService, TreeManagerTreeType } from '../../../project-subsection/services/tree-manager.service';
import { ShareLinkTreeService } from '../../../project-subsection/services/share-link-tree.service';
import { FileDisplayNamePipe } from '../../pipes/file-display-name.pipe';
import { EmitterService } from '../../services/emitter.service';
import { KpfTreeService } from '../../../project-subsection/services/kpf-tree.service';
import { GeneralUtilsService } from '../../services/general-utils.service';
import { EMPTY_UUID } from '../../constants';
import { ReleaseTreeService } from '../../../project-subsection/services/release-tree-service';
import {
  KpfTreeState,
  ReleaseTreeState,
  ShareLinkTreeState,
  SubsectionTreeState,
} from '../../../project-subsection/models';

@Component({
  selector: 'mtm-breadcrumb-project',
  templateUrl: './mtm-breadcrumb-project.component.html',
  styleUrl: './mtm-breadcrumb-project.component.scss',
  standalone: false,
})
export class MtmBreadcrumbProjectComponent implements OnInit, OnDestroy {
  @Output() selectNode: EventEmitter<BaseTreeNode> = new EventEmitter<BaseTreeNode>();
  @Input() isDashboard: boolean = false;

  private readonly breadcrumbCompactBreakpointWithVersion = 1069;
  private readonly breadcrumbCompactBreakpointWithoutVersion = 998;
  private readonly viewport = globalThis as unknown as Window;
  private viewportWidth = 0;
  treeType: TreeManagerTreeType;
  private treeState: TreeState<BaseTreeNode> | null = null;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private shareLinkRoot: BaseTreeNode | null = null;
  private subsectionRoot: BaseTreeNode | null = null;
  private kpfRoot: BaseTreeNode | null = null;
  private releaseRoot: BaseTreeNode | null = null;

  isMobileView: boolean = false;
  isCompactView: boolean = false;
  breadcrumbPath: BaseTreeNode[] = [];
  dropdownNodeStatusMap: Map<string, boolean> = new Map<string, boolean>();
  readonly treeNodeType: typeof TreeNodeType = TreeNodeType;

  private _isWidgetCollapsed: boolean = true;
  get isWidgetCollapsed(): boolean {
    return this._isWidgetCollapsed;
  }

  @Input() set isWidgetCollapsed(value: boolean) {
    if (value !== undefined && value !== this._isWidgetCollapsed) {
      this._isWidgetCollapsed = value;
      this.updateBreadcrumbPath();
    }
  }

  get root(): BaseTreeNode | null {
    return this.treeState?.root;
  }

  get selectedNode(): BaseTreeNode | null {
    return this.treeState?.selectedNode;
  }

  constructor(
    private readonly fileDisplayNamePipe: FileDisplayNamePipe,
    private readonly generalUtilsService: GeneralUtilsService,
    private readonly treeManagerService: TreeManagerService,
    private readonly shareLinkTreeService: ShareLinkTreeService,
    private readonly subsectionTreeService: SubsectionTreeService,
    private readonly releaseTreeService: ReleaseTreeService,
    private readonly kpfTreeService: KpfTreeService
  ) {}

  ngOnInit(): void {
    this.treeManagerService
      .getCurrentTreeState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: TreeState<BaseTreeNode>) => {
        this.treeState = state;
        this.updateBreadcrumbPath();
      });

    this.treeManagerService
      .getActiveTreeType()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((treeType: TreeManagerTreeType) => {
        this.treeType = treeType;
        this.updateBreadcrumbPath();
      });

    this.shareLinkTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: ShareLinkTreeState) => {
        this.shareLinkRoot = state.root;
        this.updateBreadcrumbPath();
      });

    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: ReleaseTreeState) => {
        this.releaseRoot = state.root;
        if (this.releaseRoot) {
          this.updateBreadcrumbPath();
        }
      });

    this.subsectionTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: SubsectionTreeState) => {
        this.subsectionRoot = this.generalUtilsService.customCloneDeep(state.root);
        this.updateBreadcrumbPath();
      });

    EmitterService.get('TOGGLE:MOBILE_VIEW').subscribe((data: { isMobileView?: boolean }) => {
      if (data.isMobileView !== undefined && data.isMobileView !== null) {
        this.isMobileView = data.isMobileView;
        this.updateCompactView();
      }
    });

    fromEvent(this.viewport, 'resize')
      .pipe(
        map(() => this.viewport.innerWidth),
        startWith(this.viewport.innerWidth),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((viewportWidth) => {
        this.viewportWidth = viewportWidth;
        this.updateCompactView();
      });

    this.kpfTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state: KpfTreeState) => {
        this.kpfRoot = this.generalUtilsService.customCloneDeep(state.root);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onNodeClick(node: BaseTreeNode, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectNode.emit(node);
  }

  public shouldShowNode(index: number): boolean {
    if (this.isCompactView) {
      return index === this.breadcrumbPath.length - 1;
    }

    if (this.breadcrumbPath.length <= 3) {
      return true;
    }
    return index === 0 || index >= this.breadcrumbPath.length - 2;
  }

  public shouldShowEllipsis(index: number): boolean {
    if (this.isCompactView) {
      return index === 0 && this.breadcrumbPath.length > 1;
    }

    return index === 1 && this.breadcrumbPath.length > 3;
  }

  public getHiddenNodes(): BaseTreeNode[] {
    if (this.isCompactView) {
      return this.breadcrumbPath.slice(0, -1);
    }

    if (this.breadcrumbPath.length <= 3) {
      return [];
    }
    return this.breadcrumbPath.slice(1, -2);
  }

  public isSubsectionVersionNode(node: BaseTreeNode): boolean {
    return isSubsectionVersionNode(node);
  }

  public shouldSkipVersionNode(node: BaseTreeNode): boolean {
    const hasSiblings: boolean = !!(node.parent?.children && node.parent.children.length > 1);
    return !hasSiblings;
  }

  private updateDashboardBreadcrumbPath(): void {
    this.breadcrumbPath = [];
    this.buildPathFromSelectedNode();
    this.processKpfRootNode();
    this.breadcrumbPath = this.breadcrumbPath.filter((n): n is BaseTreeNode => n != null);
  }

  private buildPathFromSelectedNode(): void {
    // Build the path from selected node up to the subsection root
    let currentNode: BaseTreeNode | null = this.generalUtilsService.customCloneDeep(this.selectedNode);
    while (currentNode) {
      const isVersion: boolean = isSubsectionVersionNode(currentNode);
      const hasSiblings: boolean = !!(currentNode.parent?.children && currentNode.parent.children.length > 1);

      if (!(isVersion && !hasSiblings)) {
        this.breadcrumbPath.unshift(currentNode);
      }

      currentNode = this.getParentNode(currentNode);
    }
  }

  private processKpfRootNode(): void {
    if (this.breadcrumbPath.length === 0) {
      return;
    }

    if (isKpfRootNode(this.breadcrumbPath[0])) {
      this.handleKpfRootAsFirstNode();
    } else if (isProjectRootNode(this.breadcrumbPath[0])) {
      this.handleProjectRootAsFirstNode();
    }
  }

  private handleKpfRootAsFirstNode(): void {
    const subsectionRoot: BaseTreeNode = this.generalUtilsService.customCloneDeep(this.subsectionRoot);
    const roots: BaseTreeNode[] = subsectionRoot.children.filter((c: BaseTreeNode) => isKpfRootNode(c));
    if (roots.length > 0) {
      roots.forEach((root: BaseTreeNode) => {
        subsectionRoot.children.splice(subsectionRoot.children.indexOf(root), 1);
        root.parent = null;
      });
    }

    subsectionRoot.children.push(this.breadcrumbPath[0]);
    this.breadcrumbPath[0].parent = subsectionRoot;
    this.breadcrumbPath.unshift(subsectionRoot);
  }

  private handleProjectRootAsFirstNode(): void {
    const projectRoot: ProjectNode = this.breadcrumbPath[0] as ProjectNode;
    const roots: BaseTreeNode[] = projectRoot.children.filter((c: BaseTreeNode) => isKpfRootNode(c));
    if (roots.length === 0) {
      if (this.kpfRoot) {
        const kpfRoot: BaseTreeNode = this.generalUtilsService.customCloneDeep(this.kpfRoot);
        projectRoot.children.push(kpfRoot);
        kpfRoot.parent = projectRoot;
      } else {
        const kpfRootNode: KpfFolderNode = {
          id: EMPTY_UUID,
          name: 'Key Project Files',
          type: TreeNodeType.KpfRoot,
          isExpandable: true,
          isSelected: false,
          isExpanded: false,
          children: [] as ProjectNode[],
          parent: projectRoot,
          depth: projectRoot.depth + 1,
        };
        projectRoot.children.push(kpfRootNode);
        kpfRootNode.parent = projectRoot;
      }
    }
  }

  private updateBreadcrumbPath(): void {
    this.breadcrumbPath = [];

    const subsectionRoot: ProjectRootNode = this.generalUtilsService.customCloneDeep(
      this.subsectionRoot
    ) as ProjectRootNode;
    const currentNode: BaseTreeNode | null = this.generalUtilsService.customCloneDeep(this.selectedNode);

    if (!currentNode && this.subsectionRoot) {
      this.breadcrumbPath = subsectionRoot != null ? [subsectionRoot] : [];
      return;
    }

    this.buildPathFromSelectedNode();

    // Add the project node as the root only if buildPathFromSelectedNode didn't already include it
    if (this.breadcrumbPath.length === 0 || !isProjectRootNode(this.breadcrumbPath[0])) {
      this.breadcrumbPath.unshift(subsectionRoot);
    }
    this.reorganizeKpfRoot(this.breadcrumbPath[0] as ProjectRootNode);
    this.breadcrumbPath = this.breadcrumbPath.filter((n): n is BaseTreeNode => n != null);
  }

  private isRootNodeType(node: BaseTreeNode): boolean {
    return (
      node.type === TreeNodeType.SubsectionRoot ||
      node.type === TreeNodeType.KpfRoot ||
      node.type === TreeNodeType.ShareLinkRoot ||
      node.type === TreeNodeType.ReleaseRoot
    );
  }

  private shouldAddSubsectionRoot(): boolean {
    return (
      this.treeType === TreeManagerTreeType.ShareLink ||
      this.treeType === TreeManagerTreeType.ShareLinkPreview ||
      this.treeType === TreeManagerTreeType.Kpf ||
      this.treeType === TreeManagerTreeType.Release
    );
  }

  private reorganizeKpfRoot(projectNode: ProjectRootNode): void {
    if (!projectNode) {
      return;
    }

    const kpfRoot: BaseTreeNode | undefined = projectNode.children.find(
      (child: BaseTreeNode) => child.type === TreeNodeType.KpfRoot
    );
    const kpfRootIndex: number = projectNode.children.findIndex(
      (child: BaseTreeNode) => child.type === TreeNodeType.KpfRoot
    );

    if (kpfRootIndex !== -1) {
      projectNode.children.splice(kpfRootIndex, 1);
      const subsectionRoot: BaseTreeNode | undefined = this.breadcrumbPath.find(
        (node: BaseTreeNode) => node.type === TreeNodeType.SubsectionRoot
      );
      if (subsectionRoot) {
        // Check if nodes don't already exist before adding them
        if (!subsectionRoot.children.some((child: BaseTreeNode) => child.type === TreeNodeType.ShareLinkRoot)) {
          subsectionRoot.children.push(this.shareLinkRoot);
        }
        if (kpfRoot && !subsectionRoot.children.some((child: BaseTreeNode) => child.type === TreeNodeType.KpfRoot)) {
          subsectionRoot.children.push(kpfRoot);
        }
        if (
          this.releaseRoot &&
          !subsectionRoot.children.some((child: BaseTreeNode) => child.type === TreeNodeType.ReleaseRoot)
        ) {
          subsectionRoot.children.unshift(this.releaseRoot);
        }
      }
    }

    this.updateCompactView();
  }

  public decodeNodeName(node: BaseTreeNode | null | undefined): string {
    if (node == null) {
      return '';
    }
    try {
      const decodedName: string = decodeURIComponent(node.name).replace(/\/$/, '');
      return isSubsectionFileNode(node) ? this.fileDisplayNamePipe.transform(decodedName) : decodedName;
    } catch {
      return isSubsectionFileNode(node) ? this.fileDisplayNamePipe.transform(node.name) : node.name;
    }
  }

  private getParentNode(node: BaseTreeNode): BaseTreeNode | null {
    return node.parent || null;
  }

  public onDropdownOpenChange(nodeId: string, isOpen: boolean): void {
    this.dropdownNodeStatusMap.set(nodeId, isOpen);
  }

  private updateCompactView(): void {
    const hasVersionNode = this.breadcrumbPath.some((node) => node && isSubsectionVersionNode(node));
    const isNarrowForVersion = this.viewportWidth < this.breadcrumbCompactBreakpointWithVersion;
    const isNarrowWithoutVersion = this.viewportWidth < this.breadcrumbCompactBreakpointWithoutVersion;
    this.isCompactView = this.isMobileView || (hasVersionNode ? isNarrowForVersion : isNarrowWithoutVersion);
  }
}
